import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { FaFire, FaSortAmountDown, FaTag } from "react-icons/fa";
import SummaryApi from '../common';
import VerticalCard from '../components/VerticalCard';
import { request } from '../helpers/request';

const HotSale = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Filters State
    const [selectCategory, setSelectCategory] = useState({});
    const [selectBrand, setSelectBrand] = useState({});
    const [sortBy, setSortBy] = useState("");

    // Derived Filters (populated from initial fetch of all sale items)
    const [saleCategories, setSaleCategories] = useState([]);
    const [saleBrands, setSaleBrands] = useState([]);

    // Initial load to get all sale items and populate filters
    useEffect(() => {
        const fetchAllSaleItems = async () => {
            try {
                // Fetch ALL sale products first to determine what categories/brands are actually on sale
                const response = await request(SummaryApi.filterProduct, {
                    body: JSON.stringify({
                        isSale: true
                    })
                });
                const result = await response.json();
                
                if (result.success && result.data) {
                    const products = result.data;
                    
                    // Extract unique categories
                    const uniqueCategories = [...new Set(products.map(p => p.category))].sort();
                    setSaleCategories(uniqueCategories);

                    // Extract unique brands
                    const uniqueBrands = [...new Set(products.map(p => p.brandName))].sort();
                    setSaleBrands(uniqueBrands);
                }
            } catch (error) {
                console.error("Error initializing sale page:", error);
            }
        };

        fetchAllSaleItems();
    }, []);

    // Main data fetching based on selected filters
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const categoryList = Object.keys(selectCategory).filter(key => selectCategory[key]);
            const brandList = Object.keys(selectBrand).filter(key => selectBrand[key]);

            const response = await request(SummaryApi.filterProduct, {
                body: JSON.stringify({
                    category: categoryList,
                    brandName: brandList,
                    isSale: true
                })
            });

            const dataResponse = await response.json();
            let products = dataResponse?.data || [];

            // Client-side sorting
            if (sortBy === 'asc') {
                products.sort((a, b) => a.sellingPrice - b.sellingPrice);
            } else if (sortBy === 'dsc') {
                products.sort((a, b) => b.sellingPrice - a.sellingPrice);
            }

            setData(products);
        } catch (error) {
            console.error("Error fetching filtered sale products:", error);
        } finally {
            setLoading(false);
        }
    }, [selectCategory, selectBrand, sortBy]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSelectCategory = (category, checked) => {
        setSelectCategory((prev) => ({
            ...prev,
            [category]: checked
        }));
    };

    const handleSelectBrand = (brand, checked) => {
        setSelectBrand((prev) => ({
            ...prev,
            [brand]: checked
        }));
    };

    const handleOnChangeSortBy = (e) => {
        setSortBy(e.target.value);
    };

    const clearFilters = () => {
        setSelectCategory({});
        setSelectBrand({});
        setSortBy("");
    };

    return (
        <div className='bg-[#fcfcff] min-h-screen'>
            {/* High-Impact Hero Banner */}
            <div className='bg-primary relative overflow-hidden h-[340px] flex items-center mb-12'>
                {/* Animated Background Layers */}
                <div className='absolute inset-0 z-0'>
                    <div className='absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,_rgba(255,77,77,0.15),transparent_70%)]' />
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.05 }}
                        className='absolute inset-0'
                        style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '40px 40px' }}
                    />
                </div>

                <div className='container mx-auto px-6 relative z-10'>
                    <div className='max-w-4xl'>
                        <motion.div 
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            className='inline-flex items-center gap-3 px-4 py-2 bg-accent/10 backdrop-blur-md rounded-full border border-accent/20 mb-6'
                        >
                            <FaFire className='text-accent text-sm animate-pulse' />
                            <span className='text-accent text-xs font-black uppercase tracking-widest'>Limited Time Deals</span>
                        </motion.div>
                        
                        <motion.h1 
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.1 }}
                            className='text-6xl md:text-8xl font-black text-white mb-6 tracking-tighter'
                        >
                            THE <span className='text-transparent bg-clip-text bg-gradient-to-r from-accent to-red-400'>HOTEST</span> <br/>SALE IS HERE
                        </motion.h1>
                        
                        <motion.p 
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className='text-xl text-white/60 font-medium max-w-xl leading-relaxed'
                        >
                            Discover premium riding gear with significant price drops. 
                            From top-tier helmets to essential accessories, grab them before they're gone.
                        </motion.p>
                    </div>
                </div>

                {/* Floating Elements */}
                <motion.div 
                    animate={{ y: [0, -20, 0] }}
                    transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                    className='absolute right-[15%] top-1/4 hidden lg:block opacity-10'
                >
                    <FaFire className='text-[200px] text-white' />
                </motion.div>
            </div>

            <div className='container mx-auto px-6 pb-24'>
                
                {/* Quick Selection Bars */}
                <div className='space-y-8 mb-12'>
                    {/* Categories Bar */}
                    <div className='space-y-4'>
                        <div className='flex items-center justify-between'>
                            <h3 className='text-xs font-black uppercase tracking-[0.2em] text-gray-400'>Browse Categories</h3>
                            {Object.values(selectCategory).some(v => v) && (
                                <button onClick={() => setSelectCategory({})} className='text-[10px] font-bold text-accent hover:underline'>Reset Categories</button>
                            )}
                        </div>
                        <div className='flex flex-wrap gap-3'>
                            {saleCategories.map((category) => (
                                <button
                                    key={category}
                                    onClick={() => handleSelectCategory(category, !selectCategory[category])}
                                    className={`px-6 py-3 rounded-2xl text-sm font-bold transition-all border-2 ${
                                        selectCategory[category] 
                                        ? 'bg-primary border-primary text-white shadow-xl shadow-primary/20 scale-105' 
                                        : 'bg-white border-gray-100 text-gray-500 hover:border-accent/30 hover:text-primary'
                                    }`}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Brands Bar */}
                    <div className='space-y-4 border-t border-gray-100 pt-8'>
                        <div className='flex items-center justify-between'>
                            <h3 className='text-xs font-black uppercase tracking-[0.2em] text-gray-400'>Top Sale Brands</h3>
                             {Object.values(selectBrand).some(v => v) && (
                                <button onClick={() => setSelectBrand({})} className='text-[10px] font-bold text-accent hover:underline'>Reset Brands</button>
                            )}
                        </div>
                        <div className='flex flex-wrap gap-2.5'>
                            {saleBrands.map((brand) => (
                                <button
                                    key={brand}
                                    onClick={() => handleSelectBrand(brand, !selectBrand[brand])}
                                    className={`px-4 py-2 rounded-xl text-xs font-black transition-all border ${
                                        selectBrand[brand] 
                                        ? 'bg-accent border-accent text-white shadow-lg shadow-accent/20' 
                                        : 'bg-white border-gray-200 text-gray-400 hover:border-primary hover:text-primary'
                                    }`}
                                >
                                    {brand}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Results and Sorting */}
                <div className='flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 border-b border-gray-100 pb-8'>
                    <div className='flex items-center gap-3'>
                        <div className='w-1 h-8 bg-accent rounded-full' />
                        <div>
                             <h2 className='text-2xl font-black text-primary'>Exclusive Offers</h2>
                             <p className='text-xs font-bold text-gray-400 uppercase tracking-widest'>{data.length} products found</p>
                        </div>
                    </div>
                    
                    <div className='flex items-center gap-4'>
                        <div className='relative'>
                            <select 
                                value={sortBy} 
                                onChange={handleOnChangeSortBy}
                                className='appearance-none h-12 pl-6 pr-12 bg-white border border-gray-200 rounded-2xl font-bold text-sm shadow-sm outline-none focus:border-accent transition-all cursor-pointer min-w-[200px]'
                            >
                                <option value="" disabled>Sort Products</option>
                                <option value="asc">Price: Low to High</option>
                                <option value="dsc">Price: High to Low</option>
                            </select>
                            <div className='absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none'>
                                <FaSortAmountDown className='text-gray-400 text-xs' />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Product Grid */}
                <div className='flex-1'>
                    <AnimatePresence mode='wait'>
                        {loading ? (
                            <motion.div 
                                key="loading"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8'
                            >
                                {Array(8).fill(0).map((_, i) => (
                                    <div key={i} className='bg-white rounded-[2rem] h-[400px] animate-pulse border border-gray-100' />
                                ))}
                            </motion.div>
                        ) : data.length > 0 ? (
                            <motion.div
                                key="content"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <VerticalCard data={data} loading={false} />
                            </motion.div>
                        ) : (
                            <motion.div 
                                key="empty"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className='h-[40vh] flex flex-col items-center justify-center bg-white rounded-[3rem] border border-gray-100 text-center p-12 shadow-sm'
                            >
                                <div className='w-20 h-20 bg-accent/5 text-accent rounded-full flex items-center justify-center mb-6'>
                                    <FaTag className='text-3xl' />
                                </div>
                                <h3 className='text-2xl font-black text-primary mb-2'>No Matches Found</h3>
                                <p className='text-gray-400 mb-8 max-w-xs text-sm font-medium'>We couldn't find any sale items matching your current filters.</p>
                                <button onClick={clearFilters} className='px-10 py-4 bg-primary text-white text-sm font-bold rounded-full hover:bg-accent transition-all shadow-xl shadow-primary/20'>Clear All Filters</button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* Newsletter / CTA Section */}
            <div className='container mx-auto px-6 mb-24'>
                <div className='bg-gradient-to-br from-primary to-[#1a1a1a] rounded-[3rem] p-8 md:p-16 relative overflow-hidden'>
                    <div className='relative z-10 grid md:grid-cols-2 items-center gap-12'>
                        <div>
                            <h2 className='text-3xl md:text-5xl font-black text-white mb-6 leading-tight'>Never Miss A <br/><span className='text-accent'>Price Drop</span> Again</h2>
                            <p className='text-white/60 text-lg mb-8'>Subscribe to our notification list and get instant alerts when your favorite brands go on sale.</p>
                            <div className='flex gap-3 max-w-md'>
                                <input type="text" placeholder='Email address' className='flex-1 h-14 rounded-2xl bg-white/5 border border-white/10 px-6 text-white text-sm outline-none focus:border-accent transition-all' />
                                <button className='px-8 h-14 bg-white text-primary font-bold rounded-2xl hover:bg-accent hover:text-white transition-all'>Join</button>
                            </div>
                        </div>
                        <div className='hidden md:block'>
                             <div className='grid grid-cols-2 gap-4'>
                                {saleCategories.slice(0, 4).map((cat, i) => (
                                    <div key={i} className='bg-white/5 backdrop-blur-sm border border-white/5 p-6 rounded-3xl'>
                                        <p className='text-accent text-xs font-black mb-2'>TOP SALE</p>
                                        <h4 className='text-white font-bold capitalize'>{cat}</h4>
                                    </div>
                                ))}
                             </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HotSale;

